if (!customElements.get('search-radio-group')) {

class SearchRadioGroup extends HTMLElement {
    constructor() {
      super();
      this.inputElement = null;
      this.radioGroup = null;
      this.showMoreButtonElement = null;
      this.parentDisplayForShowMore = null;
      this.detailsElement = null;

      this.boundFilterItems = this.filterItems.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandleDetailsToggle = this.handleDetailsToggle.bind(this);
    }

    connectedCallback() {
      this.radioGroup = this.querySelector(".radio-group");
      if (!this.radioGroup) {
        // console.error("Missing .radio-group inside <search-radio-group>.");
        return;
      }
      this.inputElement = this.querySelector('input[type="search"]');
      if (this.inputElement) {
        this.inputElement.addEventListener("input", this.boundFilterItems);
        this.inputElement.addEventListener("keydown", this.boundHandleKeydown);
      }

      this.detailsElement = this.closest('details');
      if (this.detailsElement) {
        this.detailsElement.addEventListener('toggle', this.boundHandleDetailsToggle);
      }

      // Find the show-more-button and its operating parent display
      // It's expected that search-radio-group and show-more-button are siblings within a .parent-display
      const commonParentDisplay = this.closest('.parent-display');
      if (commonParentDisplay) {
        this.showMoreButtonElement = commonParentDisplay.querySelector('show-more-button');
        if (this.showMoreButtonElement) {
          this.parentDisplayForShowMore = commonParentDisplay;
        }
      }

      this.initStyles();
      this.filterItems();
    }

    disconnectedCallback() {
      if (this.inputElement) {
        this.inputElement.removeEventListener("input", this.boundFilterItems);
        this.inputElement.removeEventListener("keydown", this.boundHandleKeydown);
        this.inputElement = null;
      }
      if (this.detailsElement) {
        this.detailsElement.removeEventListener('toggle', this.boundHandleDetailsToggle);
        this.detailsElement = null;
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Enter') return;
      event.preventDefault();

      const visibleRadios = this.radioGroup.querySelectorAll('.radio-option:not(.hidden) input[type="radio"]');
      if (visibleRadios.length !== 1) return;

      const radio = visibleRadios[0];
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleDetailsToggle() {
      if (!this.detailsElement?.open) return;
      if (!window.matchMedia('(min-width: 750px)').matches) return;
      if (!this.inputElement) return;

      requestAnimationFrame(() => {
        this.inputElement?.focus({ preventScroll: true });
      });
    }

    initStyles() {
      this.radioGroup.style.margin = "0";
      this.radioGroup.style.padding = "0";
    }

    filterItems() {
      const query = this.inputElement?.value.toLowerCase().trim() || "";
      const options = Array.from(this.radioGroup.querySelectorAll(".radio-option"));

      if (query) {
        // Search query is active
        if (this.showMoreButtonElement) {
          this.showMoreButtonElement.style.display = 'none';
        }
        options.forEach(option => {
          const text = option.querySelector("label")?.textContent.toLowerCase().trim() || "";
          const isMatch = this.fuzzyMatch(text, query);
          option.classList.toggle('hidden', !isMatch); // Show if match, hide if not
        });
      } else {
        // Search query is empty - restore show-more state
        // First, ensure all options are potentially visible from search perspective
        options.forEach(option => option.classList.remove('hidden'));

        if (this.showMoreButtonElement && this.parentDisplayForShowMore) {
          this.showMoreButtonElement.style.display = ''; // Restore button visibility

          const showMoreLabel = this.showMoreButtonElement.querySelector('.label-show-more');
          // If .label-show-more is visible (not hidden), it means show-more is in its "collapsed" state.
          const isShowMoreCollapsed = showMoreLabel && !showMoreLabel.classList.contains('hidden');
          
          const itemsToManageByShowMore = this.parentDisplayForShowMore.querySelectorAll('.show-more-item');
          itemsToManageByShowMore.forEach(item => {
            // If show-more is collapsed, hide these items.
            // If show-more is expanded (isShowMoreCollapsed is false), ensure they are not hidden.
            item.classList.toggle('hidden', isShowMoreCollapsed);
          });
        } else {
          // No show-more button or its context, ensure all items are visible (as search is clear)
          // This was already done by `options.forEach(option => option.classList.remove('hidden'));` earlier
        }
      }
    }

    fuzzyMatch(text, query) {
      if (!query) return true;
      let ti = 0, qi = 0;
      while (ti < text.length && qi < query.length) {
        if (text[ti] === query[qi]) qi++;
        ti++;
      }
      return qi === query.length;
    }
  }

  customElements.define("search-radio-group", SearchRadioGroup);
  
}